import { WSQueryCache } from "@ws-react-query";
import { Payee, PayeeEngagementType } from "@wingspanhq/wingspan-api-sdk";
import { payeeEngagementsApi } from "@api";
import { queriesPayeeEngagements, queriesPayeeRows } from "@query";
import { QUERY_PAYEE } from "../../../query/payee/keys";
import {
  bulkActionFactory,
  BulkActionProgress,
  useForegroundBulkAction
} from "../../BulkActions";
import { useXWingspanPayerId } from "../routes/RoutePayeeDetails/useXWingspanPayerId";
import { CreatePayeeInput, useCreatePayee } from "./useCreatePayee";

export interface CreateContractorBulkEngagement {
  id: string;
  name: string;
  engagementType: PayeeEngagementType;
}

export interface CreateContractorBulkActionInput {
  payeeCreationData: CreatePayeeInput;
  engagements: CreateContractorBulkEngagement[];
}

export interface CreateContractorBulkActionSettings {
  toDetailsOnSuccess: boolean;
}

type Response = {
  payee?: Payee;
  toDetails?: boolean;
};

export const useCreateContractorBulkAction = (): ((
  input: CreateContractorBulkActionInput,
  settings: CreateContractorBulkActionSettings
) => Promise<Response>) => {
  const runBulkAction = useForegroundBulkAction();
  const xWingspanPayerId = useXWingspanPayerId();
  const [createPayee] = useCreatePayee();

  return async ({ payeeCreationData, engagements }, { toDetailsOnSuccess }) => {
    const total = 1 + engagements.length;
    const response: Response = {};

    await runBulkAction({
      actionName: "Add contractor and assignments",
      itemCount: total,
      skipConfirm: true,
      nextSecondaryAction: {
        label: toDetailsOnSuccess ? "Close" : "Continue"
      },
      nextAction: toDetailsOnSuccess
        ? {
            label: "Go to details",
            onClick() {
              response.toDetails = true;
            }
          }
        : undefined,
      prepare: async () => ({ payeeCreationData, engagements }),
      process: async (data, onProgress): Promise<BulkActionProgress> => {
        onProgress({
          total,
          processed: 0,
          successful: 0,
          failed: 0,
          progressMessage: "Creating contractor..."
        });

        // v3 create + invite (+ v1 payer-owned bridge). Returns `undefined` on
        // error (the mutation swallows it — throwOnError defaults to false), so
        // branch on the falsy result rather than a try/catch.
        const payee = await createPayee(data.payeeCreationData);

        if (!payee) {
          onProgress({
            total,
            processed: 1,
            successful: 0,
            failed: 1,
            progressMessage: "Failed to create contractor",
            failedDescriptions: ["Create contractor"]
          });
          return {
            total,
            processed: 1,
            successful: 0,
            failed: 1,
            failedDescriptions: ["Create contractor"]
          };
        }

        response.payee = payee;

        const contractorSuccessDescriptions = ["Contractor created"];

        onProgress({
          total,
          processed: 1,
          successful: 1,
          failed: 0,
          progressMessage: "Contractor created",
          successDescriptions: contractorSuccessDescriptions
        });

        const bulk = bulkActionFactory<CreateContractorBulkEngagement>({
          action: engagement =>
            payeeEngagementsApi.createPayeeEngagement({
              payeeId: response.payee!.id!,
              payeeEngagementCreate: {
                engagementId: engagement.id,
                engagementType: engagement.engagementType
              },
              xWingspanAccount: xWingspanPayerId
            }),
          getSuccessDescription: engagement => `Assigned "${engagement.name}"`,
          getFailedDescription: engagement => `Assign "${engagement.name}"`,
          getProgressMessage: (_, progress) =>
            `Assigning engagements (${progress.processed} of ${progress.total})`,
          onProgress: progress =>
            onProgress({
              total,
              processed: 1 + progress.processed,
              successful: 1 + progress.successful,
              failed: progress.failed,
              progressMessage: progress.progressMessage,
              successDescriptions: [
                ...contractorSuccessDescriptions,
                ...(progress.successDescriptions ?? [])
              ],
              failedDescriptions: progress.failedDescriptions
            }),
          onFinish: () => {
            // The v1 `QUERY_PAYEE_ROWS` / `QUERY_PAYEE_ROWS_SUMMARY` keys were
            // invalidated here too. Dropped: their hooks throw `NotMigrated`, and
            // v3 has no row-summary endpoint at all (`PayeeRowsApi` exposes only
            // `searchPayeeRows`, SDK 3.52.6).
            WSQueryCache.invalidateQueries(
              queriesPayeeRows.searchPayeeRows.queryKeyBase
            );
            WSQueryCache.invalidateQueries(
              queriesPayeeEngagements.getPayeeEngagement.queryKeyBase
            );
            WSQueryCache.invalidateQueries(
              queriesPayeeEngagements.listPayeeEngagementsByPayer.queryKeyBase
            );
            WSQueryCache.invalidateQueries(
              queriesPayeeEngagements.listPayeeEngagements.queryKeyBase
            );
            WSQueryCache.invalidateQueries(
              queriesPayeeEngagements.listPayeeEngagementsByPayer.queryKeyBase
            );
            WSQueryCache.invalidateQueries(QUERY_PAYEE);
            WSQueryCache.invalidateQueries(
              queriesPayeeEngagements.listPayeeEngagements.queryKeyBase
            );
          }
        });

        const { successItems, failedItems } = await bulk.run(data.engagements);

        return {
          total,
          processed: 1 + successItems.length + failedItems.length,
          successful: 1 + successItems.length,
          failed: failedItems.length,
          successDescriptions: [
            ...contractorSuccessDescriptions,
            ...successItems.map(e => `Assigned "${e.name}"`)
          ],
          failedDescriptions: failedItems.map(e => `Assign "${e.name}"`)
        };
      }
    });

    return response;
  };
};
